import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CACHE_TIME, STALE_TIME } from "../ApiProvider";
import { api } from "./api";
const USE_LIST_EXTERNAL_MATCHES_BY_TEAM_ID = "useListExternalMatchesByTeamId";
const USE_LIST_EXTERNAL_MATCHES_BY_PLAYER_ID = "useListExternalMatchesByPlayerId";
const USE_LIST_OPPONENT_TEAMS = "useListOpponentTeams";
const USE_RETRIEVE_EXTERNAL_MATCH = "useRetrieveExternalMatch";
export const useRetrieveExternalMatch = ({ id, enabled, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_EXTERNAL_MATCH, id],
        queryFn: async () => {
            if (!id)
                throw new Error("External Match ID is not defined!");
            const response = await api.get(`/external-matches/${id}/`);
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: !!id && enabled,
    });
};
export const useUpdateExternalMatch = ({ id, team, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!id) {
                throw new Error("External Match ID is not defined!");
            }
            return api.put(`/external-matches/${id}/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            if (team === undefined) {
                throw new Error("Team must be defined!");
            }
            queryClient.invalidateQueries([
                USE_LIST_EXTERNAL_MATCHES_BY_TEAM_ID,
                team.id,
            ]);
            team.players?.forEach((playerId) => {
                queryClient.invalidateQueries([
                    USE_LIST_EXTERNAL_MATCHES_BY_PLAYER_ID,
                    playerId,
                ]);
            });
            queryClient.invalidateQueries([USE_RETRIEVE_EXTERNAL_MATCH, id]);
            onSuccess(data);
        },
        onError,
    });
};
export const useListOpponentTeams = () => {
    return useQuery({
        queryKey: [USE_LIST_OPPONENT_TEAMS],
        queryFn: async () => {
            const response = await api.get("/opponent-teams/");
            return response.data;
        },
        cacheTime: 1000 * 60,
        staleTime: 1000 * 60,
    });
};
export const useCreateExternalMatch = ({ team, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.post("/external-matches/", data);
        },
        onMutate,
        onSuccess: (data) => {
            if (team === undefined) {
                throw new Error("Team must be defined!");
            }
            queryClient.invalidateQueries([
                USE_LIST_EXTERNAL_MATCHES_BY_TEAM_ID,
                team.id,
            ]);
            team.players?.forEach((playerId) => {
                queryClient.invalidateQueries([
                    USE_LIST_EXTERNAL_MATCHES_BY_PLAYER_ID,
                    playerId,
                ]);
            });
            onSuccess(data);
        },
        onError,
    });
};
export const useDeleteExternalMatch = ({ externalMatch, team, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            if (!externalMatch) {
                throw new Error("ExternalMatch is undefined");
            }
            return api.delete(`/external-matches/${externalMatch.id}/`);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([
                USE_LIST_EXTERNAL_MATCHES_BY_TEAM_ID,
                externalMatch?.team,
            ]);
            team?.players?.forEach((playerId) => {
                queryClient.invalidateQueries([
                    USE_LIST_EXTERNAL_MATCHES_BY_PLAYER_ID,
                    playerId,
                ]);
            });
            queryClient.removeQueries([
                USE_RETRIEVE_EXTERNAL_MATCH,
                externalMatch?.id,
            ]);
            onSuccess(data);
        },
        onError,
    });
};
export const useSubmitExternalMatchStats = (externalMatch) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!externalMatch?.id) {
                throw new Error("External match must be defined!");
            }
            return api.post(`/external-matches/${externalMatch.id}/submit-stats/`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([
                USE_LIST_EXTERNAL_MATCHES_BY_TEAM_ID,
                externalMatch.team,
            ]);
            (externalMatch.players || []).forEach((player) => {
                queryClient.invalidateQueries([
                    USE_LIST_EXTERNAL_MATCHES_BY_PLAYER_ID,
                    player.id,
                ]);
            });
            queryClient.invalidateQueries([
                USE_RETRIEVE_EXTERNAL_MATCH,
                externalMatch.id,
            ]);
        },
    });
};
